import * as React from 'react';
import { VaiIcon } from '@vaisala/rockhopper-design-tokens';
import { addClass, Bem } from '../../utils/css.js';
import { canUseDom } from '../../utils/common.js';
import { Heading } from '../Heading/Heading.js';
import { FlexItem } from '../Flex/FlexItem.js';
import { Icon } from '../Icon/Icon.js';
import { useId } from '../../utils/hooks/useId.js';
import './Accordion.scss.js';

const EXPANSION_DURATION = 400;
const COLLAPSE_START_DELAY = 10;
var AnimationState;
(function(AnimationState2) {
  AnimationState2["collapsed"] = "collapsed";
  AnimationState2["expanding"] = "expanding";
  AnimationState2["expanded"] = "expanded";
  AnimationState2["preparingToCollapse"] = "preparing-to-collapse";
})(AnimationState || (AnimationState = {}));
const bem = Bem.create("vai-accordion");
const defaultRenderExpandIcon = (expanded) => /* @__PURE__ */ React.createElement(Icon, {
  className: bem.element("title")("icon"),
  name: expanded ? VaiIcon.ChevronDown : VaiIcon.ChevronRight
});
const Accordion = React.memo(React.forwardRef((props, ref) => {
  const {
    htmlId,
    title,
    icon,
    isExpanded,
    openByDefault = false,
    onChangeAccordionExpanded,
    accordionKey,
    dataTa = "vai-accordion",
    summary,
    summaryWidth = "30%",
    children,
    mode = "auto",
    className,
    onAccordionFocus,
    renderExpandIcon = defaultRenderExpandIcon,
    _buttonRef
  } = props;
  const id = useId(htmlId, ["accordion"]);
  const linkId = id.copyWith(["title", "link"]).toString();
  const headingId = id.copyWith(["title"]).toHtmlId();
  const contentId = id.copyWith(["content"]).toString();
  const [internallyExpanded, setInternallyExpanded] = React.useState(openByDefault);
  const expanded = React.useMemo(() => isExpanded === void 0 ? internallyExpanded : isExpanded, [isExpanded, internallyExpanded]);
  const containerClass = addClass(bem.block(expanded && "expanded", mode, icon && "has-icon"), className);
  const animationState = useAccordionAnimation(expanded);
  const handleFocus = React.useCallback(() => {
    onAccordionFocus?.(accordionKey);
  }, [onAccordionFocus, accordionKey]);
  const handleClick = React.useCallback(() => {
    onChangeAccordionExpanded?.(!expanded, accordionKey);
    if (isExpanded === void 0) {
      setInternallyExpanded((prev) => !prev);
    }
  }, [onChangeAccordionExpanded, isExpanded, expanded, accordionKey]);
  return /* @__PURE__ */ React.createElement("div", {
    id: id.toString(),
    "data-ta": dataTa,
    className: containerClass,
    ref
  }, /* @__PURE__ */ React.createElement("button", {
    id: linkId,
    "data-ta": "button",
    className: bem.element("link")(expanded && "expanded"),
    onClick: handleClick,
    "aria-expanded": expanded ? "true" : "false",
    "aria-controls": contentId,
    type: "button",
    onFocus: handleFocus,
    ref: _buttonRef
  }, /* @__PURE__ */ React.createElement(Heading, {
    htmlId: headingId,
    className: bem.element("title")(),
    level: 4
  }, /* @__PURE__ */ React.createElement("div", {
    className: bem.element("title")("icon-wrapper")
  }, renderExpandIcon(expanded)), title), summary && /* @__PURE__ */ React.createElement(FlexItem, {
    flexBasis: summaryWidth,
    className: bem.element("summary")()
  }, summary), icon && /* @__PURE__ */ React.createElement("div", {
    className: bem.element("secondary-icon")()
  }, icon)), /* @__PURE__ */ React.createElement("div", {
    id: contentId,
    className: bem.element("content")(animationState),
    "aria-labelledby": headingId?.toString()
  }, /* @__PURE__ */ React.createElement("div", {
    className: bem.element("inner-content")(!expanded && "hidden")
  }, children)));
}));
function useAccordionAnimation(isExpanded) {
  const [animationState, setAnimationState] = React.useState(isExpanded ? AnimationState.expanded : AnimationState.collapsed);
  const timeoutIdRef = React.useRef();
  const setDelayedAnimationState = React.useCallback((state, delay) => {
    if (!canUseDom()) {
      return;
    }
    if (timeoutIdRef.current) {
      window.clearTimeout(timeoutIdRef.current);
    }
    timeoutIdRef.current = window.setTimeout(() => setAnimationState(state), delay);
  }, []);
  React.useEffect(() => {
    const shouldExpand = animationState === AnimationState.collapsed && isExpanded;
    const shouldCollapse = animationState === AnimationState.expanded && !isExpanded;
    if (shouldExpand) {
      setAnimationState(AnimationState.expanding);
      setDelayedAnimationState(AnimationState.expanded, EXPANSION_DURATION);
    } else if (shouldCollapse) {
      setAnimationState(AnimationState.preparingToCollapse);
      setDelayedAnimationState(AnimationState.collapsed, COLLAPSE_START_DELAY);
    }
    return () => {
      timeoutIdRef.current && clearTimeout(timeoutIdRef.current);
    };
  }, [isExpanded, animationState, setAnimationState, setDelayedAnimationState]);
  return animationState;
}

export { Accordion, AnimationState };
